import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/authStore';
import { getAd, type AdSlot } from '@/api/ads';
import { startAdTracker, stopAdTracker } from './tracker';

/** How long a served ad stays cached before the slot asks for a fresh one.
 *  Long enough that scrolling doesn't refetch, short enough that pausing a
 *  campaign in the admin console takes effect within a session. */
const AD_STALE_MS = 5 * 60 * 1000;

/** Fetches the ad for a slot. Returns undefined while loading and null when the
 *  slot is unsold — callers render nothing in both cases. */
export function useAdSlot(slot: AdSlot) {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['ad-slot', slot],
    queryFn: () => getAd(token as string, slot),
    enabled: !!token,
    staleTime: AD_STALE_MS,
    // An ad failing to load must never retry aggressively or surface an error;
    // the slot simply stays empty.
    retry: false,
  });
}

/** Keeps the impression/click flush loop running for the signed-in session.
 *  Mounted once, at the app root. */
export function useAdTracker() {
  const token = useAuthStore((s) => s.accessToken);
  useEffect(() => {
    if (!token) {
      stopAdTracker();
      return;
    }
    startAdTracker(token);
    return () => stopAdTracker();
  }, [token]);
}
